import { ChangeDetectionStrategy, Component, computed, inject, OnInit, signal } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import ZoomMtgEmbedded, { EmbeddedClient } from '@zoom/meetingsdk/embedded';
import { catchError, of } from 'rxjs';

import { BaseComponent } from '../../../../core/abstractions/base.component';
import { BlobModel } from '../../../../models/blob.model';
import { RolesEnum } from '../../../../models/enums/roles.enum';
import { GeneralMeetingModel } from '../../../../models/general-meeting.model';
import { OptionalType } from '../../../../models/types/optional.type';
import { GeneralMeetingCheckInService } from '../../../../services/general-meeting-check-in.service';
import { BlobsResource } from '../../../../services/resources/blobs.resource';
import { GeneralMeetingsResource } from '../../../../services/resources/general-meetings.resource';
import { TranslationService } from '../../../../services/translation.service';
import { UserSessionService } from '../../../../services/user-session.service';
import { LiveStreamRequestToSpeakDialog } from './live-stream-request-to-speak/live-stream-request-to-speak.dialog';

@Component({
    selector: 'sl-live-stream',
    templateUrl: './live-stream.component.html',
    styleUrl: './live-stream.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class LiveStreamComponent extends BaseComponent implements OnInit {
    readonly isSaving = signal(false);
    readonly isLoading = signal(true);
    readonly isEditing = signal(false);
    readonly canEdit = signal(false);

    readonly isLoadingCheckedIn = signal(true);
    readonly isCheckedIn = signal(false);

    readonly iframeUrl = signal<OptionalType<SafeResourceUrl>>(undefined);

    readonly zoomId = signal<OptionalType<string>>(undefined);
    readonly zoomPassword = signal<OptionalType<string>>(undefined);
    readonly isZoomLoading = signal(false);
    readonly isZoomConnected = signal(false);
    readonly isZoomConnecting = signal(false);
    readonly canZoomConnect = signal(false);

    readonly currentMeeting = signal<OptionalType<GeneralMeetingModel>>(undefined);

    readonly hasZoomSettings = computed(() => !!this.zoomId() && !!this.zoomPassword());
    readonly canRequestToSpeak = computed(
        () =>
            this.hasZoomSettings() && !this.isZoomLoading() && this.canZoomConnect() && !this.isZoomConnecting() && !this.isZoomConnected()
    );

    readonly formGroup = new FormGroup({
        stream: new FormGroup({
            de: new FormControl(''),
            en: new FormControl(''),
            fr: new FormControl(''),
            it: new FormControl('')
        }),
        zoom: new FormGroup({
            id: new FormControl(''),
            password: new FormControl('')
        })
    });

    private zoomClient: typeof EmbeddedClient;
    private zoomSignature?: string;
    private styleObserver?: MutationObserver;

    private readonly streamKey = 'live-stream-settings';
    private readonly sdkKey = 'axsOJSCga3LTcVFi3TafRY4OJFiBeefK60op';

    private readonly translationService = inject(TranslationService);
    private readonly userSessionService = inject(UserSessionService);
    private readonly generalMeetingsResource = inject(GeneralMeetingsResource);
    private readonly generalMeetingCheckInService = inject(GeneralMeetingCheckInService);
    private readonly blobsResource = inject(BlobsResource);
    private readonly domSanitizer = inject(DomSanitizer);
    private readonly matDialog = inject(MatDialog);

    ngOnInit(): void {
        this.isLoading.set(true);

        const checkInStatusChangeSub = this.generalMeetingCheckInService.checkInStatusChange().subscribe(isCheckedIn => {
            this.isCheckedIn.set(isCheckedIn);
        });

        const getStatusSub = this.generalMeetingCheckInService.getStatus().subscribe(isCheckedIn => {
            this.isCheckedIn.set(isCheckedIn);
            this.isLoadingCheckedIn.set(false);
        });
        this.addSubscriptions(checkInStatusChangeSub, getStatusSub);

        this.canEdit.set(this.userSessionService.roleId >= RolesEnum.Operator);

        this.blobsResource
            .download(this.streamKey)
            .pipe(
                catchError(() => {
                    this.isLoading.set(false);
                    return of({ value: '' });
                })
            )
            .subscribe(content => {
                if (!!content.value) {
                    try {
                        const currentData = JSON.parse(atob(content.value)) as OptionalType<ReturnType<typeof this.formGroup.getRawValue>>;
                        if (!currentData) {
                            return;
                        }

                        this.formGroup.patchValue(currentData);
                        this.setSettings(currentData);
                        this.initZoomMeetingSession();
                    } catch {
                        // ignore
                    }
                }

                this.isLoading.set(false);
            });
    }

    checkIn(): void {
        this.isLoadingCheckedIn.set(true);
        this.generalMeetingCheckInService.checkIn(() => this.isLoadingCheckedIn.set(false));
    }

    requestToSpeak(): void {
        const dialogRef = this.matDialog.open(LiveStreamRequestToSpeakDialog, {
            width: '600px',
            maxWidth: '95vw'
        });

        dialogRef.afterClosed().subscribe(result => {
            if (!!result) {
                this.joinZoomMeeting();
            }
        });
    }

    save(): void {
        if (this.isSaving()) {
            return;
        }

        this.isSaving.set(true);
        this.setSettings(this.formGroup.getRawValue());
        this.blobsResource
            .upload(
                new BlobModel({
                    key: this.streamKey,
                    data: btoa(JSON.stringify(this.formGroup.getRawValue())),
                    mimeType: 'text/plain'
                })
            )
            .subscribe()
            .add(() => {
                this.isSaving.set(false);
                this.isEditing.set(false);
            });

        this.initZoomMeetingSession();
    }

    private async joinZoomMeeting(): Promise<void> {
        const zoomId = this.zoomId();
        const zoomPassword = this.zoomPassword();

        if (!this.zoomSignature || !zoomId || !zoomPassword) {
            return;
        }

        try {
            const userInfo = this.userSessionService.userInfo;
            const username = userInfo ? `${userInfo.username} (${userInfo.shareholderId})` : 'unknown';
            await this.zoomClient.join({
                sdkKey: this.sdkKey,
                signature: this.zoomSignature,
                meetingNumber: zoomId,
                password: zoomPassword,
                userName: username
            });

            this.isZoomConnecting.set(false);
            this.isZoomConnected.set(true);

            // make sure zoom draggable window cannot be outside bounds
            const zoomDraggableContainer = document.querySelector('#meetingContainer .react-draggable') as HTMLElement;

            if (!!zoomDraggableContainer) {
                this.styleObserver = new MutationObserver(() => {
                    // update transform
                    const transform = zoomDraggableContainer.style.transform;
                    let [x, y] = transform
                        .replace(/[^\d-\.\,]/g, '')
                        .split(',')
                        .map(d => parseFloat(d));
                    x = Math.min(Math.max(x, 0), document.body.clientWidth - zoomDraggableContainer.clientWidth);
                    y = Math.min(Math.max(y, 0), document.body.clientHeight - zoomDraggableContainer.clientHeight);
                    zoomDraggableContainer.style.transform = `translate(${x}px, ${y}px)`;
                });
                this.styleObserver.observe(zoomDraggableContainer, { attributeFilter: ['style'] });
            }
        } catch (e: any) {
            this.isZoomConnecting.set(false);
            console.error('Could not join zoom meeting. Error:', e.type, '- reason:', e.reason);
        }
    }

    private setSettings(data: ReturnType<typeof this.formGroup.getRawValue>): void {
        const code = this.translationService.getCurrent().code as 'de' | 'en' | 'it' | 'fr';
        const url = data?.stream?.[code];
        this.iframeUrl.set(!!url ? this.domSanitizer.bypassSecurityTrustResourceUrl(url) : undefined);
        this.zoomId.set(data.zoom?.id || undefined);
        this.zoomPassword.set(data.zoom?.password || undefined);
    }

    private onConnectionChanged(payload: any): void {
        // on disconnect
        if (payload.state === 'Closed') {
            this.styleObserver?.disconnect();
            this.isZoomConnected.set(false);
        }
    }

    private async initZoomMeetingSession(): Promise<void> {
        if (!this.hasZoomSettings()) {
            return;
        }

        this.loadZoomSignature();

        this.zoomClient = ZoomMtgEmbedded.createClient();
        let intfLang: 'de-DE' | 'en-US' | 'fr-FR' | 'it-IT' = 'de-DE';

        switch (this.userSessionService.userInfo?.languageId) {
            case 'en':
                intfLang = 'en-US';
                break;
            case 'it':
                intfLang = 'it-IT';
                break;
            case 'fr':
                intfLang = 'fr-FR';
                break;
            default:
                break;
        }

        // add meeting element to body
        const meetingContainerElement = document.createElement('div');
        meetingContainerElement.id = 'meetingContainer';
        document.body.appendChild(meetingContainerElement);

        try {
            await this.zoomClient.init({
                zoomAppRoot: meetingContainerElement,
                language: intfLang,
                patchJsMedia: true,
                customize: {
                    meetingInfo: ['topic', 'host', 'mn', 'pwd', 'telPwd', 'invite', 'participant', 'dc', 'enctype']
                }
            });

            this.isZoomLoading.set(false);
            this.canZoomConnect.set(true);

            // listen to connection changes
            this.zoomClient.on('connection-change', this.onConnectionChanged.bind(this));
        } catch (e: any) {
            this.isZoomLoading.set(false);
            // TODO show error to user
            console.error('Could not initialize zoom client. Error:', e.type, '- reason:', e.reason);
        }
    }

    private loadZoomSignature(): void {
        const meetingId = Number(this.zoomId()?.replaceAll(' ', ''));

        if (isNaN(meetingId)) {
            this.zoomSignature = undefined;
            return;
        }

        this.generalMeetingsResource.requestZoomSignature(meetingId).subscribe(response => {
            this.zoomSignature = response.value?.signature;
        });
    }
}
